import { Typography } from "antd";
import { FC, useContext, useMemo } from "react";
import {
  Inline,
  useAppContext,
  useClientContext,
  useInlineContext,
  useNavigation,
  View,
} from "@data-root/react";

import {
  SchemaItemProps,
  useAsyncCallback,
  useField,
  UseRowContext,
  useRows,
  UseRowsContext,
} from "@data-root/react";
import {
  Filter,
  RefRowsSchemaItem,
  View as IView,
  RowWithEmbeddedMetadata,
} from "@data-root/types";
import { DeckRows } from "@data-root/plugin-view-deck";
import { v4 } from "uuid";
import { Remarks } from "../../remarks";
import { ErrorView } from "@data-root/error-boundary";

const { Title } = Typography;

export const DeckPresentation: FC<SchemaItemProps<RefRowsSchemaItem>> = ({
  item,
}) => {
  const { core } = useClientContext();
  const { appSpecifier, getTable } = useAppContext();
  const { navigation } = useNavigation();
  const { view } = useInlineContext();

  const { loaded, rowId } = useContext(UseRowContext)!;
  const { displayName, remarks } = useField(item);

  const filters = useMemo<Filter[]>(() => {
    if (!loaded) return [];

    return [
      {
        id: item.refField,
        type: "text",
        value: rowId,
      },
    ];
  }, [item.refField, loaded, rowId]);

  const useRefRowsContext = useRows(core, appSpecifier, item.ref, filters);
  const {
    getContext,
    loaded: ready,
    displayRows,
    fetch,
    fetching,
    totalRows,
    error,
  } = useRefRowsContext;

  const refTable = useMemo(() => getTable(item.ref), [getTable, item.ref]);

  const onClick = useAsyncCallback(
    async (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.navigate(item.detailViewId!, {
        rowId: refRow.id,
      });
    },
    [item.detailViewId, navigation]
  );

  const onEdit = useAsyncCallback(
    async (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.navigate(item.formViewId!, {
        rowId: refRow.id,
      });
    },
    [item.formViewId, navigation]
  );

  const primaryHeader = useMemo(() => {
    if (
      view.customizations &&
      view.customizations[item.id] &&
      view.customizations[item.id].primaryHeader
    )
      return view.customizations[item.id].primaryHeader;

    return refTable.label;
  }, [item.id, refTable.label, view.customizations]);

  const secondaryHeader = useMemo(() => {
    if (
      view.customizations &&
      view.customizations[item.id] &&
      view.customizations[item.id].secondaryHeader
    )
      return view.customizations[item.id].secondaryHeader;

    return refTable.secondaryLabel;
  }, [item.id, refTable.secondaryLabel, view.customizations]);

  const image = useMemo(() => {
    if (
      view.customizations &&
      view.customizations[item.id] &&
      view.customizations[item.id].image
    )
      return view.customizations[item.id].image;

    return refTable.imageLabel;
  }, [item.id, refTable.imageLabel, view.customizations]);

  const inlineView = useMemo<IView>(
    () => ({
      id: `inline:${item.ref}`,
      name: `Inline ${item.ref}`,
      description: `Inline ${item.ref}`,
      type: "Deck",
      tableId: item.ref,
      icon: "TableOutlined",
      options: {
        primaryHeader,
        secondaryHeader,
        image,
        actionIds: ["EDIT"],
      },
    }),
    [image, item.ref, primaryHeader, secondaryHeader]
  );

  const containerId = useMemo(() => v4(), []);

  if (error) return <ErrorView error={error} />;

  return (
    <UseRowsContext.Provider value={useRefRowsContext}>
      <View style={{ width: "100%" }}>
        <Title level={5}>{displayName}</Title>

        <Inline
          id={containerId}
          table={refTable}
          view={inlineView}
          onClick={onClick}
          onEdit={onEdit}
        >
          <DeckRows
            child
            containerId={containerId}
            getContext={getContext}
            loaded={loaded && ready}
            rows={displayRows}
            onFetch={fetch}
            showGroups={false}
            totalRows={totalRows}
            fetching={fetching}
          />
        </Inline>

        <Remarks texts={remarks} />
      </View>
    </UseRowsContext.Provider>
  );
};
